/**
 * cw-a11y.js — keyboard and screen-reader behaviour for the Kenetik header.
 *
 * Replaces the jQuery click-only wiring that used to live in cw-custom.js.
 * Loaded (deferred) by layout/theme.liquid and layout/cw-layout.liquid.
 *
 * Covers:
 *   - Main menu toggle (Douglass v. VitaNav ¶38(d)): real <button>, aria-expanded,
 *     focus moves into the panel, Tab is trapped while open on mobile, Esc closes,
 *     focus returns to the toggle, closed panel is `inert` so its links leave the tab order.
 *   - Submenu toggles: aria-expanded + Esc to close.
 *   - Help control (¶38(b)): opens Gorgias chat when the SDK is available, otherwise
 *     follows its href to the contact page.
 *
 * Vanilla JS, no dependencies. Idempotent — safe to re-run after section re-render
 * in the theme editor (listens for shopify:section:load).
 */
(function () {
  'use strict';

  var MOBILE_MAX = 767; // matches the off-canvas breakpoint in cw-header.css
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function isMobile() {
    return window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)').matches;
  }

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  /** Keep Tab / Shift+Tab inside `container` while it is open. Returns a teardown fn. */
  function trapFocus(container) {
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var items = focusables(container);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKeydown);
    return function () { container.removeEventListener('keydown', onKeydown); };
  }

  /* ------------------------------------------------------------------ */
  /* Main menu                                                           */
  /* ------------------------------------------------------------------ */
  function initMenu(header) {
    var toggle = header.querySelector('.js-open-menu');
    var nav = header.querySelector('#cw-main-nav');
    if (!toggle || !nav || toggle.dataset.a11yBound) return;
    toggle.dataset.a11yBound = '1';

    var closeBtn = nav.querySelector('.js-close-menu');
    var releaseTrap = null;
    var lastFocus = null;

    function syncInert() {
      // Off-canvas on mobile: when closed, remove the panel's 20 links from the tab order.
      // Desktop renders the nav inline, so it must stay reachable.
      if (isMobile() && !document.body.classList.contains('open-menu')) {
        nav.setAttribute('inert', '');
        nav.setAttribute('aria-hidden', 'true');
      } else {
        nav.removeAttribute('inert');
        nav.removeAttribute('aria-hidden');
      }
    }

    function open() {
      lastFocus = document.activeElement;
      document.body.classList.add('open-menu');
      nav.removeAttribute('inert');
      nav.removeAttribute('aria-hidden');
      toggle.setAttribute('aria-expanded', 'true');
      var first = closeBtn || focusables(nav)[0];
      // wait for the CSS transition so the element is focusable
      window.setTimeout(function () { if (first) first.focus(); }, 50);
      releaseTrap = trapFocus(nav);
    }

    function close(returnFocus) {
      if (!document.body.classList.contains('open-menu')) return;
      document.body.classList.remove('open-menu');
      toggle.setAttribute('aria-expanded', 'false');
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      // collapse any open submenu
      nav.querySelectorAll('.sub-menu.opan-menu').forEach(function (sm) { sm.classList.remove('opan-menu'); });
      nav.querySelectorAll('.js-submenu-button[aria-expanded="true"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      syncInert();
      if (returnFocus !== false) (lastFocus || toggle).focus();
    }

    toggle.addEventListener('click', function () {
      if (document.body.classList.contains('open-menu')) close(); else open();
    });
    if (closeBtn) closeBtn.addEventListener('click', function () { close(); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('open-menu')) close();
    });

    // click outside closes (preserves existing behaviour), without stealing focus
    document.addEventListener('click', function (e) {
      if (!document.body.classList.contains('open-menu')) return;
      if (toggle.contains(e.target) || nav.contains(e.target)) return;
      close(false);
    });

    /* Submenus */
    nav.querySelectorAll('.js-submenu-button').forEach(function (btn) {
      var li = btn.closest('li');
      var panel = li && li.querySelector('.sub-menu');
      if (!panel) return;
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        panel.classList.toggle('opan-menu', !expanded);
        if (!expanded) {
          var firstLink = panel.querySelector('a, button');
          if (firstLink) window.setTimeout(function () { firstLink.focus(); }, 50);
        }
      });
      var back = panel.querySelector('.back-to-menu');
      if (back) back.addEventListener('click', function () {
        panel.classList.remove('opan-menu');
        btn.setAttribute('aria-expanded', 'false');
        btn.focus();
      });
      panel.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          e.stopPropagation();
          panel.classList.remove('opan-menu');
          btn.setAttribute('aria-expanded', 'false');
          btn.focus();
        }
      });
      // Desktop: hovering out should also reset state so aria-expanded stays truthful
      li.addEventListener('mouseleave', function () {
        if (!isMobile()) { panel.classList.remove('opan-menu'); btn.setAttribute('aria-expanded', 'false'); }
      });
    });

    syncInert();
    window.addEventListener('resize', syncInert);
  }

  /* ------------------------------------------------------------------ */
  /* Help / chat                                                         */
  /* ------------------------------------------------------------------ */
  function initHelp(header) {
    var help = header.querySelector('.js-help-chat');
    if (!help || help.dataset.a11yBound) return;
    help.dataset.a11yBound = '1';
    help.addEventListener('click', function (e) {
      var g = window.GorgiasChat;
      if (g && typeof g.open === 'function') {
        e.preventDefault();
        try { g.open(); } catch (err) { window.location.href = help.href; }
      }
      // else: follow the href to the contact page
    });
  }

  /* ------------------------------------------------------------------ */
  function init() {
    document.querySelectorAll('.cw-header').forEach(function (header) {
      initMenu(header);
      initHelp(header);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  // expose for cw-a11y-cart.js (PR-2) and tests
  window.cwA11y = window.cwA11y || {};
  window.cwA11y.trapFocus = trapFocus;
  window.cwA11y.focusables = focusables;
})();
